import { ILabelViewModel } from '#/DefaultViewExtensionInterfaces';
import * as DVEInterfaces from '#/DefaultViewExtensionInterfaces';
import ControlViewModel from '@/ViewModels/ControlViewModel';
import ComponentManager from '@/Management/ComponentManager';
import ResourceManager from '@/Management/ResourceManager';
import Utilities from '@/Utility/Utilities';

/**
 * Das ViewModel für die Label
 * @param {object} params - Ein Objekt mit Optionen für das Control
 * @constructor
 */
export default class LabelViewModel extends ControlViewModel implements ILabelViewModel {
    /**
     * Gibt an, ob das Label einen Fehler darstellt
     */
    public isErrorLabel: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob das Label eine Info darstellt
     */
    public isInfoLabel: KnockoutObservable<boolean>;

    /**
     * Gibt an, ob ein nicht gefundener ergonomischer Bezeichner neu geladen werden soll
     */
    public tryToLoad: boolean;

    /**
     * Gibt an, ob das Label ein HistorizationBanner ist
     */
    public isHistorizationBanner: KnockoutObservable<boolean>;

    /**
     * Enthält den übergebenen Caption-Parameter.
     */
    public originalCaption: KnockoutObservable<string>;

    public refreshCaption: KnockoutObservable<boolean>;

    /**
     * Der Text des Labels; berechnet sich dynamisch aus ergName und originalCaption
     */
    public caption: KnockoutObservable<string>;

    /**
     * Der ergonomische Bezeichner des Labels
     */
    public ergName: KnockoutObservable<string>;

    public resolvedErgName: KnockoutObservable<string>;

    /**
     * Der Platzhalter falls der Text des Labels leer ist
     */
    public placeholder: KnockoutObservable<string>;

    /**
     * Im ergonomischen Bezeichner enthaltene Platzhalter können ersetzt werden
     */
    public replacements: Record<string, string | ILabelViewModel>;

    public prefix?: LabelViewModel;
    public postfix?: LabelViewModel;

    /**
     * Gibt an, ob der Text gekürzt werden soll
     */
    public isWordWrapped: KnockoutObservable<boolean>;

    /**
     * Die Länge, ab der der Text gewrapped werden soll
     */
    public wordWrapAppend: KnockoutObservable<string>;

    /**
     * Die Länge, ab der der Text gewrapped werden soll
     */
    public maxLength: KnockoutObservable<number>;

    /**
     * Gibt an, ob der Text klein ausgeben werden soll
     */
    public toLowerCase: KnockoutObservable<boolean>;

    constructor(params: DVEInterfaces.ILabelViewModelConstructorParameter) {
        super(params);
        this.isErrorLabel = this.observableOrDefault(params.isErrorLabel, false);
        this.isInfoLabel = this.observableOrDefault(params.isInfoLabel, false);
        this.tryToLoad = this.paramOrDefault(params.tryToLoad, true);
        this.isHistorizationBanner = this.observableOrDefault(params.isHistorizationBanner, false);
        this.ergName = this.observableOrDefault(params.ergName, '');
        this.resolvedErgName = this.observableOrDefault('');
        this.placeholder = this.observableOrDefault(params.placeholder, '');
        this.replacements = this.paramOrDefault(params.replacements, {});
        this.prefix = this.createAffixViewModel(params.prefix);
        this.postfix = this.createAffixViewModel(params.postfix);
        this.isWordWrapped = this.observableOrDefault(params.isWordWrapped, false);
        this.wordWrapAppend = this.observableOrDefault(params.wordWrapAppend, '…');
        this.maxLength = this.observableOrDefault(params.maxLength, 30);
        this.originalCaption = this.observableOrDefault(params.caption, '');
        this.refreshCaption = this.observableOrDefault(true);
        this.toLowerCase = this.observableOrDefault(params.toLowerCase, false);
        this.caption = this.defineComputed({
            read: () => {
                const refreshCaption = this.refreshCaption(); // refresh computed trigger

                const resolvedErgname = this.resolvedErgName();
                const origCaption = this.originalCaption();
                const isWordWrapped = this.isWordWrapped();

                let caption = resolvedErgname ? resolvedErgname : origCaption;

                if (isWordWrapped === true) {
                    caption = this.wordWrap(caption, ' ');
                }

                if(this.toLowerCase() && caption)
                    caption = caption.toLowerCase();

                return caption;
            },
            write: (newCaption: string) => {
                this.originalCaption(newCaption);
            }
        });
        this.defineSubscription(this.ergName, () => { this.resolveErgName(); });
        this.defineSubscription(ResourceManager.getInstance().resources, () => { this.resolveErgName(); });

        this.resolveErgName();
    }

    private createAffixViewModel(param: LabelViewModel | ObservableOrDefault<string>): LabelViewModel | undefined {
        if (!param) {
            return undefined;
        }

        if (param instanceof LabelViewModel) {
            return param;
        }

        const caption = this.observableOrDefault(param);
        return new LabelViewModel({
            caption: caption,
            isVisible: this.defineComputed(() => {
                const value = caption();
                return value != null && value != '';
            }),
            prefix: undefined,
            postfix: undefined,
        });
    }

    /**
     * Diese Methode aktualisiert den aufgelösten ErgName auf der Grundlage des aktuellen ErgNames
     */
    private resolveErgName() {
        const ergName = this.ergName();
        if (ergName) {
            let resource = ResourceManager.getInstance().get(ergName);
            if (resource == ergName && this.tryToLoad === true) {
                ResourceManager.getInstance().requestResourceItem(ergName)
                    .done((resolvedErgName) => {
                        this.updateResolvedErgName(ergName, resolvedErgName)
                    });
            } else {
                this.updateResolvedErgName(ergName, resource)
            }
        }
        else {
            this.resolvedErgName('');
        }
    }

    /**
     * Aktualisiert den aufgelösten ErgName auf der Grundlage des angegebenen ErgName und der Ressource.
     */
    private updateResolvedErgName(ergName: string, resource: string): void {
        if (this.isDisposed || this.ergName() != ergName) {
            return;
        }

        //Wenn eine Ressource geladen ist, werden die Platzhalter in der Ressource ersetzt.
        if (resource && ergName != resource) {
            resource = this.applyReplacements(resource);
        }
        this.resolvedErgName(resource)
    }

    private applyReplacements(input: string): string {
        //Wenn nichts zu ersetzen ist, brauchen wir auch nichts zu tun.
        if (Object.keys(this.replacements).length == 0)
            return input;

        _.each(this.replacements, (replacement, placeholder) => {
            const placeholderRegEx = this.wrapPlaceholder(placeholder);
            const sanitizedReplacement = this.sanitizeReplacement(replacement);

            input = input.replace(placeholderRegEx, sanitizedReplacement);
        });

        return input;
    }

    private wrapPlaceholder(placeholder) {
        return new RegExp('\\$\\$' + placeholder + '\\$\\$');
    }

    private sanitizeReplacement(replacement) {
        if (replacement instanceof LabelViewModel) {
            return replacement.caption();
        }
        return replacement;
    }

    /*
         * Hilfsfunktion zum Beschränken von Wort-Längen
         */
    private wordWrap(str, delimiter) {
        const wordWrapAppend = this.wordWrapAppend();
        const maxLength = this.maxLength();

        if (str.length <= maxLength) {
            return str;
        }

        let trimmedStr = str.substr(0, maxLength + delimiter.length);
        const lastDelimIndex = trimmedStr.lastIndexOf(delimiter);

        if (lastDelimIndex >= 0) {
            trimmedStr = trimmedStr.substr(0, lastDelimIndex);
        }

        if (trimmedStr) {
            trimmedStr += wordWrapAppend;
        }

        return trimmedStr;
    }

    public static create(ergName: string): LabelViewModel {
        return new LabelViewModel({ ergName: ergName });
    }

    /**
     * @override
     */
    protected disposeInternal() : void {
        for (const key in this.replacements) {
            const currentReplacement = this.replacements[key] as ILabelViewModel;
            if (currentReplacement.dispose != null) {
                currentReplacement.dispose();
            }
        }
        super.disposeInternal();
    }
}

ComponentManager.setViewModel('LabelViewModel', LabelViewModel, 'RADPresentation.HTMLClient.DefaultViewExtensions');